import React from 'react';

import { Logout } from '@mui/icons-material';
import { Link, Stack, Tooltip } from '@mui/material';

import { toggleExpandingStateManually } from '@/app/log-viewer/highlighting/HighlightableStringExpandableContainer';
import { HighlightableStringView } from '@/app/log-viewer/highlighting/HighlightableStringView';
import { NetworkRequestOutcome } from '@/parsing/parsers/common/NetworkRequestOutcome';

import { ContextualEvents } from './components/ContextualEvents';
import { GoogleMapsLink } from './components/GoogleMapsLink';
import { KibanaLink } from './components/KibanaLink';
import { NetworkRequestBody } from './components/NetworkRequestBody';
import { NetworkRequestKeyValuePairDetails } from './components/NetworkRequestKeyValuePairDetails';
import { LogEntryNetworkCellModel, LogEntryNetworkCellModelData } from './LogEntryNetworkCellModel';
import { NetworkRequestColorCodingTooltipContent } from './NetworkRequestColorCodingTooltipContent';

export class NetworkRequestCell extends React.Component<{ model: LogEntryNetworkCellModel }> {
  render() {
    const model = this.props.model;
    const data = this.props.model.data;
    const reqData = data.request!;

    let requestCssClassName: string;
    switch (data.outcome) {
      case NetworkRequestOutcome.Unknown:
        requestCssClassName = 'network-request-unknown';
        break;
      case NetworkRequestOutcome.Canceled:
        requestCssClassName = 'network-request-canceled';
        break;
      case NetworkRequestOutcome.Succeeded:
        requestCssClassName = 'network-request-succeeded';
        break;
      case NetworkRequestOutcome.Failed:
        requestCssClassName = 'network-request-failed';
        break;
      case NetworkRequestOutcome.ProbablyFailed:
        requestCssClassName = 'network-request-probably-failed';
        break;
    }

    return (
      <div>
        <Stack direction="row" spacing="8px" alignItems="center">
          <Tooltip
            disableInteractive
            title={<NetworkRequestColorCodingTooltipContent outcome={data.outcome} />}
          >
            <Stack
              direction="row"
              spacing="8px"
              alignItems="center"
              className={requestCssClassName}
            >
              <Logout style={{ transform: 'rotate(180deg)' }} />
              <HighlightableStringView string={reqData.title} />
            </Stack>
          </Tooltip>

          {reqData.jumpToResponseLink ? (
            <Link
              href=""
              onClick={(event) => {
                if (this.props.model.onRequestJumpToLogEntry) {
                  this.props.model.onRequestJumpToLogEntry(
                    reqData.jumpToResponseLink!.responseLogEntryIndex,
                  );
                }
                event.preventDefault();
              }}
            >
              <HighlightableStringView string={reqData.jumpToResponseLink.text} />
            </Link>
          ) : null}

          {data.kibanaLink ? (
            <KibanaLink url={data.kibanaLink.url}>
              <HighlightableStringView string={data.kibanaLink.text} />
            </KibanaLink>
          ) : null}

          {data.googleMapsLink ? (
            <GoogleMapsLink url={data.googleMapsLink.url}>
              <HighlightableStringView string={data.googleMapsLink.text} />
            </GoogleMapsLink>
          ) : null}
        </Stack>

        {data.url ? <HighlightableStringView string={data.url} /> : null}

        {reqData.canceled ? (
          <div>
            <HighlightableStringView string={reqData.canceled} />
          </div>
        ) : null}

        {data.request?.headers ? (
          <NetworkRequestKeyValuePairDetails
            dataModel={data.request.headers}
            onOpenToggle={() => {
              const change: DeepPartial<LogEntryNetworkCellModelData> = {
                request: {
                  headers: {
                    expandingState: toggleExpandingStateManually(
                      data.request!.headers!.expandingState,
                    ),
                  },
                },
              };
              model.delegate?.onLogEntryCellModelChange({
                cellModel: model,
                change,
                postProcess: false,
              });
            }}
          />
        ) : null}

        {data.request?.query ? (
          <NetworkRequestKeyValuePairDetails
            dataModel={data.request.query}
            onOpenToggle={() => {
              const change: DeepPartial<LogEntryNetworkCellModelData> = {
                request: {
                  query: {
                    expandingState: toggleExpandingStateManually(
                      data.request!.query!.expandingState,
                    ),
                  },
                },
              };
              model.delegate?.onLogEntryCellModelChange({
                cellModel: model,
                change,
                postProcess: false,
              });
            }}
          />
        ) : null}

        {reqData.body ? (
          <NetworkRequestBody
            data={reqData.body}
            onDataChange={(bodyChange) => {
              const change: DeepPartial<LogEntryNetworkCellModelData> = {
                request: {
                  body: bodyChange.change,
                },
              };
              this.props.model.delegate?.onLogEntryCellModelChange({
                cellModel: model,
                change,
                postProcess: bodyChange.postProcess,
              });
            }}
            onRequestCreateNewColumn={(jqQuery) => {
              const c = this.props.model.onRequestCreateNewColumn;
              if (c) {
                c(jqQuery);
              }
            }}
          />
        ) : null}

        {this.props.model.onRequestSurroundingEntries && this.props.model.showContextualEvents ? (
          <ContextualEvents
            currentEntryIndex={data.entryIndex}
            currentEntryLabel={`Request #${data.entryIndex + 1}`}
            onRequestSurroundingEntries={this.props.model.onRequestSurroundingEntries}
          />
        ) : null}
      </div>
    );
  }
}
